import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Plus,
  Search,
  Edit,
  Users as UsersIcon,
  User as UserIcon,
  AlertTriangle,
} from "lucide-react";
import { apiService } from "../services/api";
import {
  User,
  UserCreate,
  UserUpdate,
  isAdmin,
  canManageUsers,
  canManageSupplementaryRoles,
  supplementaryRoleLabel,
  SUPPLEMENTARY_ROLES,
} from "../types";
import { useAuth } from "../hooks/useAuth";
import UserForm from "../components/UserForm";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import Pagination from "../components/Pagination";
import toast from "react-hot-toast";

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserForm, setShowUserForm] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch users
  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery("users", apiService.getUsers, {
    enabled: canManageUsers(currentUser),
  });

  const { data: membershipRequestsData, isLoading: membershipRequestsLoading } =
    useQuery(
      "membership-requests",
      () => apiService.getMembershipRequests("requested"),
      {
        enabled: isAdmin(currentUser),
      },
    );

  // Create user mutation
  const createUserMutation = useMutation(
    (data: UserCreate) => apiService.createUser(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["users"]);
        toast.success("User created successfully");
        setShowUserForm(false);
        setSelectedUser(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to create user");
      },
    },
  );

  // Update user mutation
  const updateUserMutation = useMutation(
    ({ userId, data }: { userId: string; data: UserUpdate }) =>
      apiService.updateUser(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["users"]);
        toast.success("User updated successfully");
        setShowUserForm(false);
        setSelectedUser(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to update user");
      },
    },
  );

  const updateMembershipRequestMutation = useMutation(
    ({
      requestId,
      status,
    }: {
      requestId: string;
      status: "approved" | "rejected";
    }) => apiService.updateMembershipRequest(requestId, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["membership-requests"]);
        queryClient.invalidateQueries(["users"]);
        toast.success("Membership request updated");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to update request");
      },
    },
  );

  const allUsers = (usersData?.data as any)?.data || [];

  const supplementaryRoleMutation = useMutation(
    ({
      userId,
      roleId,
      grant,
    }: {
      userId: string;
      roleId: string;
      grant: boolean;
    }) =>
      grant
        ? apiService.grantUserRole(userId, roleId)
        : apiService.revokeUserRole(userId, roleId),
    {
      onSuccess: (_, { roleId, grant }) => {
        queryClient.invalidateQueries(["users"]);
        toast.success(
          `${supplementaryRoleLabel(roleId)} ${grant ? "granted" : "revoked"}. Takes effect at the user's next sign-in.`,
        );
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to update role");
      },
    },
  );
  const membershipRequests = useMemo(
    () =>
      (((membershipRequestsData?.data as any)?.data || []) as any[]).map(
        (request) => ({
          id: request.userId || request.id,
          name: request.name || request.user?.name || "Unknown",
          email: request.email || request.user?.email || "",
          businessArea:
            request.businessArea || request.user?.businessArea || "-",
          division: request.division || request.user?.division || "-",
        }),
      ),
    [membershipRequestsData],
  );

  // Client-side filtering and pagination
  const paginationData = useMemo(() => {
    // Filter users based on search term
    const filtered = allUsers.filter(
      (user: User) =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.businessArea?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.division?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const total = filtered.length;
    const totalPages = Math.ceil(total / itemsPerPage);

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedUsers = filtered.slice(startIndex, endIndex);

    return {
      users: paginatedUsers,
      total,
      totalPages,
    };
  }, [allUsers, searchTerm, currentPage, itemsPerPage]);

  const { users: filteredUsers, total, totalPages } = paginationData;

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowUserForm(true);
  };

  const handleFormSubmit = (data: UserCreate | UserUpdate) => {
    if (selectedUser) {
      // Update existing user
      updateUserMutation.mutate({
        userId: selectedUser.id,
        data: data as UserUpdate,
      });
    } else {
      // Create new user
      createUserMutation.mutate(data as UserCreate);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  if (!canManageUsers(currentUser)) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Access Denied
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          You need admin privileges to access this page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Users
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage system users and their permissions
          </p>
        </div>
        <button
          onClick={handleCreateUser}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Search and Filters */}
      {/* Search and Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 w-[90%] mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search users by name, email, business area, division/business unit, or role..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {isAdmin(currentUser) &&
        !membershipRequestsLoading &&
        membershipRequests.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 w-[90%] mx-auto">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">
              Pending Membership Requests
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Business Area
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Division/Business Unit
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {membershipRequests.map((request: User) => (
                    <tr
                      key={request.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        <div className="font-medium">
                          {request.name || "Unknown"}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {request.email || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {request.businessArea || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {request.division || "-"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() =>
                              updateMembershipRequestMutation.mutate({
                                requestId: request.id,
                                status: "rejected",
                              })
                            }
                            disabled={updateMembershipRequestMutation.isLoading}
                          >
                            Deny
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() =>
                              updateMembershipRequestMutation.mutate({
                                requestId: request.id,
                                status: "approved",
                              })
                            }
                            disabled={updateMembershipRequestMutation.isLoading}
                          >
                            Approve
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* Users List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 w-[90%] mx-auto">
        {usersLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Loading users...
            </p>
          </div>
        ) : usersError ? (
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Failed to load users
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Unable to load user data.
            </p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {searchTerm ? "No users found" : "No users found"}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm
                ? "Try adjusting your search terms."
                : "No users are registered yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Business Area
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Division/Business Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Additional Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Admin Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user: User) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.businessArea || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.division || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.role || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <MultiSelectDropdown
                        options={SUPPLEMENTARY_ROLES}
                        selected={user.supplementaryRoles ?? []}
                        readOnly={!canManageSupplementaryRoles(currentUser)}
                        disabled={supplementaryRoleMutation.isLoading}
                        onToggle={(roleId, grant) =>
                          supplementaryRoleMutation.mutate({
                            userId: user.id,
                            roleId,
                            grant,
                          })
                        }
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isAdmin(user)
                            ? "bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {isAdmin(user) ? "Admin" : "User"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : "Unknown"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {user.id !== currentUser?.id ? (
                          <>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-200"
                              title="Edit user"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            Current User
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[5, 10, 20, 50]}
            />
          </div>
        )}
      </div>

      {/* User Form Modal */}
      <UserForm
        user={selectedUser}
        isOpen={showUserForm}
        onClose={() => {
          setShowUserForm(false);
          setSelectedUser(null);
        }}
        onSubmit={handleFormSubmit}
        isLoading={createUserMutation.isLoading || updateUserMutation.isLoading}
      />
    </div>
  );
}
